import { markdownToClipboardHtml } from '@reflex/ui/lib/markdown-clipboard';
import { referencesToClipboardText } from '@reflex/ui/lib/references-to-clipboard';
import type { TurnBlock } from './types.js';

/**
 * Builds the clipboard source for an assistant turn: the agent's visible
 * text output, in order, separated by blank lines. Tool calls, thinking,
 * plans, and media blocks are omitted — copying a chat response should
 * yield the prose the agent wrote, not the full internal transcript.
 *
 * Returns an empty string when the turn has no text (e.g. a tool-only
 * turn), so callers can hide the copy affordance rather than offer an
 * empty copy.
 */
export function buildTurnCopyText(blocks: TurnBlock[]): string {
  return blocks
    .filter((b): b is Extract<TurnBlock, { type: 'text' }> => b.type === 'text')
    .map((b) => referencesToClipboardText(b.text).trim())
    .filter((text) => text.length > 0)
    .join('\n\n');
}

/**
 * The same prose as {@link buildTurnCopyText}, rendered to HTML for the
 * clipboard's rich-text flavor. Pasting into Slack, a doc, or an email then
 * keeps the headings, lists, links, and code the turn was rendered with
 * instead of dropping raw markdown source.
 *
 * Agent output is model-authored markdown, where a single newline is a soft
 * break — same as the `MarkdownContent` that rendered the turn on screen.
 */
export function buildTurnCopyHtml(blocks: TurnBlock[]): string {
  return markdownToClipboardHtml(buildTurnCopyText(blocks));
}
